import { z } from 'zod';

// Component schemas for Craft.js
export const craftComponentSchema = z.object({
  id: z.string(),
  type: z.string(),
  props: z.record(z.any()),
  displayName: z.string().optional(),
  custom: z.record(z.any()).optional(),
  parent: z.string().optional(),
  isCanvas: z.boolean().optional(),
  nodes: z.array(z.string()).optional(),
  linkedNodes: z.record(z.string()).optional(),
});

export const craftNodesSchema = z.record(craftComponentSchema);

// Component type enum
export const componentTypeSchema = z.enum([
  'navbar',
  'navigation-menu',
  'hero',
  'split-hero',
  'simple-header',
  'features',
  'cta',
  'footer',
  'text',
  'text-blocks',
  'image',
  'button',
  'card',
  'list',
  'form',
  'form-split',
  'team',
  'media-text',
  'pricing',
  'video',
  'title-subtitle',
  'contact-info',
  'head-content',
  'body-end',
  'html-embed',
  'html-split',
  'faq',
  'testimonials',
  'countdown-timer',
  'image-gallery',
  'image-info',
  'image-card',
  'content-area', // Special component for page layouts - marks where page content renders
  'promo-banner',
  'logocloud',
  'stats',
  'blog-preview',
  'conversion-popup',
  'compete-form',
]);

// Base component schema
export const baseComponentSchema = z.object({
  id: z.string().uuid(),
  type: componentTypeSchema,
  order: z.number().int().min(0),
  visible: z.boolean().default(true),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

// Button schema (shared across components)
export const buttonSchema = z.object({
  text: z.string().min(1, 'Button text is required'),
  url: z.string().min(1, 'Button URL is required'),
  style: z.enum(['primary', 'secondary', 'outline', 'ghost']).default('primary'),
  target: z.enum(['_self', '_blank']).default('_self').optional(),
});

// Image crop schema (shared across components with image cropping)
export const imageCropAreaSchema = z
  .object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
  })
  .optional();

export const imageCropSchema = z
  .object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
    zoom: z.number(),
    aspect: z.number(),
    croppedAreaPixels: imageCropAreaSchema,
    croppedAreaPercent: imageCropAreaSchema,
    // Image effect options
    grayscale: z.boolean().optional(),
  })
  .optional();

// Type exports
export type CraftComponent = z.infer<typeof craftComponentSchema>;
export type CraftNodes = z.infer<typeof craftNodesSchema>;
export type ComponentType = z.infer<typeof componentTypeSchema>;
export type BaseComponent = z.infer<typeof baseComponentSchema>;
export type Button = z.infer<typeof buttonSchema>;
export type ImageCropArea = z.infer<typeof imageCropAreaSchema>;
export type ImageCrop = z.infer<typeof imageCropSchema>;
